import React, {
  Dispatch,
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { cn } from '@/lib/misc/Utils'

export interface OptionsType {
  value: string
  label: string
  formattedLabel?: string | React.JSX.Element
  selected?: boolean
  disabled?: boolean
  group?: string
  groupLabel?: string | React.JSX.Element
  [key: string]: any
}

interface GenericOptionProps {
  cy?: string
  className?: string
  options: OptionsType[]
  isOpened: boolean
  setIsOpened: Dispatch<React.SetStateAction<boolean>>
  onSelectOption: (val: string, option?: OptionsType) => void
}

const GenericOption: FC<GenericOptionProps> = ({
  cy,
  className,
  isOpened,
  options,
  setIsOpened,
  onSelectOption,
}) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(-1)
  const selectedOptionRef = useRef<HTMLLIElement>(null)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpened) return

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault()
          if (options.length < 2) {
            return
          }
          setSelectedOptionIndex((prevIndex) =>
            prevIndex > 0 && prevIndex !== 0 ? prevIndex - 1 : 0
          )
          if (selectedOptionRef.current) {
            selectedOptionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            })
          }
          break
        case 'ArrowDown':
          event.preventDefault()
          if (options.length < 1) {
            return
          }
          setSelectedOptionIndex((prevIndex) =>
            prevIndex < options.length - 1 ? prevIndex + 1 : options.length - 1
          )
          if (selectedOptionRef.current) {
            selectedOptionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
          break
        case 'Enter':
          event.preventDefault()
          if (selectedOptionIndex < 0) {
            return
          }
          if (options[selectedOptionIndex]?.disabled) {
            return
          }
          if (options[selectedOptionIndex]) {
            onSelectOption(
              options[selectedOptionIndex].value,
              options[selectedOptionIndex]
            )
          }
          setIsOpened(false)
          break
        case 'Escape':
          event.preventDefault()
          setIsOpened(false)
          break
        default:
          break
      }
    },
    [isOpened, options, selectedOptionIndex, onSelectOption, setIsOpened]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false)

    return () => document.removeEventListener('keydown', handleKeyDown, false)
  }, [handleKeyDown])

  return (
    <ul className={className}>
      {options.map((el, index) => (
        <React.Fragment key={index}>
          {!!el.group && el.group !== options[index - 1]?.group && (
            <li
              role="presentation"
              className="px-3 pt-3 pb-1 text-xs font-medium uppercase tracking-wide text-neutral-50"
            >
              {el.groupLabel ?? el.group}
            </li>
          )}
          <li
            data-cy={`${cy}-${typeof el.value === 'string' ? el.value : index}`}
            {...(el.status !== undefined && { 'data-status': el.status })}
            ref={index === selectedOptionIndex ? selectedOptionRef : null}
            onClick={() => !el.disabled && onSelectOption(el.value, el)}
            aria-disabled={el.disabled || undefined}
            className={cn(
              'py-2 px-3 first:mt-1 last:mb-1 group/option mx-1 rounded-lg',
              el.disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-neutral-10 cursor-pointer',
              el.selected && 'bg-neutral-10/75',
              index === selectedOptionIndex && !el.selected && 'bg-neutral-10',
              el.className
            )}
          >
            {el.formattedLabel || el.label}
          </li>
        </React.Fragment>
      ))}
    </ul>
  )
}

export default memo(GenericOption)
